import { useState } from 'react'
import { ApiError, admin } from '../api/client'
import type { GatewaySchedule, GatewaySchedules } from '../api/types'
import { formatMinutes, parseMinutes, roundLine } from '../lib/gatewayRounds'
import { formatDateTime, formatTime } from '../lib/time'
import { useAsync } from '../lib/useAsync'

/** Gateway rounds (db/037): when each gateway's status is read. Its round
 *  minutes are learned daily from when its nodes report; an admin may set
 *  them, the gap between reads and the retries here, and clear a setting to
 *  go back to what was learned. Every read goes over the gateway's SIM. */
export function GatewayRounds() {
  const schedules = useAsync(() => admin.gatewaySchedules(), [])
  const [editing, setEditing] = useState<string>()

  if (schedules.loading) return <p className="muted">Loading…</p>
  if (schedules.error) return <p className="error">{schedules.error}</p>
  if (!schedules.data) return null
  const { defaults, gateways } = schedules.data

  return (
    <>
      <p className="muted">
        A round reads a gateway's status at its minute, then every {defaults.retry_gap_s / 60} minutes
        until every reading message that normally arrives then has (at most {defaults.retries} more reads),
        and not again until the next round. Readings the round finds are collected within a minute.
      </p>
      <table className="grid">
        <thead>
          <tr>
            <th>Gateway</th><th>Rounds at</th><th>Reads</th><th>Last day</th><th>Latest round</th><th />
          </tr>
        </thead>
        <tbody>
          {gateways.map((g) => (editing === g.code
            ? <EditRow key={g.code} gateway={g} defaults={defaults}
                       onDone={(saved) => { setEditing(undefined); if (saved) schedules.reload() }} />
            : <ViewRow key={g.code} gateway={g} onEdit={() => setEditing(g.code)} />))}
        </tbody>
      </table>
    </>
  )
}

function source(g: GatewaySchedule): string {
  if (g.override.round_minutes) return g.updated_by ? `set by ${g.updated_by}` : 'set by an admin'
  if (g.learned.round_minutes) {
    const from = g.learned.streams ? ` from ${g.learned.streams} streams` : ''
    return `learned${from}`
  }
  return 'default, nothing learned yet'
}

function ViewRow({ gateway: g, onEdit }: { gateway: GatewaySchedule; onEdit: () => void }) {
  const latest = g.latest_round
  const latestText = latest
    ? `${formatTime(latest.start)}: ${latest.reported} of ${latest.expected} expected messages, ` +
      `${latest.reads} ${latest.reads === 1 ? 'read' : 'reads'}` +
      (latest.outcome === 'gave up' ? `, missing ${latest.missing.join(', ')}` : '') +
      (latest.outcome === null ? ', in progress' : '')
    : '—'
  return (
    <tr>
      <td className="nowrap"><strong>{g.name}</strong>
        {!g.collecting && <> <span className="pill neutral">collection off</span></>}</td>
      <td><span className="mono">{formatMinutes(g.effective.round_minutes)}</span>
        <div className="muted">{source(g)}</div></td>
      <td className="nowrap">every {g.effective.retry_gap_s / 60} min, up to {g.effective.retries} more</td>
      <td>{roundLine(g.last_day)}</td>
      <td>{latestText}</td>
      <td><button type="button" className="link" onClick={onEdit}>Edit</button></td>
    </tr>
  )
}

interface EditProps {
  gateway: GatewaySchedule
  defaults: GatewaySchedules['defaults']
  onDone: (saved: boolean) => void
}

function EditRow({ gateway: g, defaults, onDone }: EditProps) {
  const [minutes, setMinutes] = useState(g.override.round_minutes ? formatMinutes(g.override.round_minutes) : '')
  const [gap, setGap] = useState(g.override.retry_gap_s ? String(g.override.retry_gap_s / 60) : '')
  const [retries, setRetries] = useState(g.override.retries === null ? '' : String(g.override.retries))
  const [error, setError] = useState<string>()
  const [busy, setBusy] = useState(false)

  async function handleSave() {
    const parsed = parseMinutes(minutes)
    if ('error' in parsed) { setError(parsed.error); return }
    const gapMin = gap.trim() === '' ? null : Number(gap)
    const tries = retries.trim() === '' ? null : Number(retries)
    if (gapMin !== null && !(gapMin >= 1 && gapMin <= 10)) { setError('The gap between reads is 1 to 10 minutes.'); return }
    if (tries !== null && !(Number.isInteger(tries) && tries >= 0 && tries <= 15)) { setError('A round may retry 0 to 15 times.'); return }
    setBusy(true)
    setError(undefined)
    try {
      await admin.updateGatewaySchedule(g.code, {
        round_minutes: parsed.minutes,
        retry_gap_s: gapMin === null ? null : Math.round(gapMin * 60),
        retries: tries,
      })
      onDone(true)
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'The change was not saved.')
    } finally {
      setBusy(false)
    }
  }

  const learned = g.learned.round_minutes ? formatMinutes(g.learned.round_minutes) : ':00'
  return (
    <tr>
      <td className="nowrap"><strong>{g.name}</strong></td>
      <td>
        <input aria-label={`Round minutes for ${g.name}`} value={minutes}
               placeholder={`learned: ${learned}`} onChange={(e) => setMinutes(e.target.value)} />
        <div className="muted">Blank uses the learned minutes{g.learned.learned_at
          ? ` (learned ${formatDateTime(g.learned.learned_at)})` : ''}.</div>
      </td>
      <td className="nowrap">
        every <input aria-label="Minutes between reads" className="narrow" value={gap}
                     placeholder={String(defaults.retry_gap_s / 60)} onChange={(e) => setGap(e.target.value)} /> min,
        up to <input aria-label="Retries" className="narrow" value={retries}
                     placeholder={String(defaults.retries)} onChange={(e) => setRetries(e.target.value)} /> more
      </td>
      <td colSpan={2}>{error && <p className="error">{error}</p>}</td>
      <td className="nowrap">
        <button type="button" className="button primary" onClick={handleSave} disabled={busy}>Save</button>{' '}
        <button type="button" className="link" onClick={() => onDone(false)} disabled={busy}>Cancel</button>
      </td>
    </tr>
  )
}
